import { ThresholdBandConfig, thresholdBandAxisHints } from '../plugins/chartjs-plugin-threshold-bands';
import { ChartOptionsConfig } from './chart-options-storage.service';

/**
 * Applies a ChartOptionsConfig's Y-axis mode to a chart.js y-scale object,
 * in place. Shared by every chart component that wires up
 * ChartOptionsButtonComponent (multi-line, scatter, dynamic-line,
 * predictive) so the mode semantics — and the "always fully rebuild, never
 * leave a stale key from a previous mode" rule that makes switching modes
 * back and forth work correctly — stay in exactly one place.
 *
 * Safe to call on:
 *  - a freshly-built options object (nothing to clear yet), or
 *  - the SAME persistent options object chart.js already holds (called
 *    again later with a different mode) — every axis-override key this
 *    function might have set on a prior call is cleared unconditionally
 *    before applying the current mode, so mode A -> B -> A leaves nothing
 *    from B behind.
 *
 * @param y the mutable `chartOptions.scales.y` object (or the live chart's
 *   `mainChart.options.scales.y`)
 * @param config current resolved ChartOptionsConfig (yAxisMode + yMin/yMax)
 * @param bandConfig threshold-bands config, if this chart has one — only
 *   relevant to 'auto-bands' mode; pass null for charts with no bands
 *   concept (in which case ChartOptionsButtonComponent's hasBands input
 *   should also be false, so 'auto-bands' is never selectable there)
 * @param suggestedMin/suggestedMax the chart's own base suggested bounds
 *   (e.g. from a `params` input) — re-applied as the 'auto-data' baseline
 *   every call, since they're not something this function owns setting once
 */
export function applyYAxisMode(
  y: Record<string, any>,
  config: ChartOptionsConfig,
  bandConfig: ThresholdBandConfig | null,
  suggestedMin?: number,
  suggestedMax?: number,
): void {
  delete y.min;
  delete y.max;
  delete y.suggestedMin;
  delete y.suggestedMax;

  if (suggestedMin != undefined) y.suggestedMin = suggestedMin;
  if (suggestedMax != undefined) y.suggestedMax = suggestedMax;

  if (config.yAxisMode === 'manual') {
    delete y.suggestedMin;
    delete y.suggestedMax;
    y.min = config.yMin;
    y.max = config.yMax;
    return;
  }

  if (config.yAxisMode === 'auto-bands' && bandConfig?.bands?.length && bandConfig.extendAxis !== false) {
    const hints = thresholdBandAxisHints(bandConfig.bands);
    if (hints) {
      const curMin = y.suggestedMin;
      const curMax = y.suggestedMax;
      y.suggestedMin = curMin != undefined ? Math.min(curMin, hints.suggestedMin) : hints.suggestedMin;
      y.suggestedMax = curMax != undefined ? Math.max(curMax, hints.suggestedMax) : hints.suggestedMax;
    }
  }
  // 'auto-data': nothing further — suggestedMin/Max applied above stand as-is.
}
